<script lang="ts" setup>
import type { Ref } from 'vue'
import { computed, inject, ref } from 'vue'
import { THEME_BACKGROUNDS, THEME_SIZE_CLASSES } from '@/constants'
import { clamp } from '@/utils'

type BACKGROUND = typeof THEME_BACKGROUNDS[number]
type SIZE = keyof typeof THEME_SIZE_CLASSES

defineProps<{
  value: boolean
  contentId: string
  chapterId: string
  prev?: boolean
  next?: boolean
}>()

const emit = defineEmits<{
  (event: 'input', value: boolean): void
  (event: 'changeBackground', value: string): void
  (event: 'changeSize', value: SIZE): void
  (event: 'chapterPrev'): void
  (event: 'chapterNext'): void
  (event: 'toc'): void
}>()

const theme = inject<Ref<{
  background: BACKGROUND
  size: SIZE
}>>('theme', ref({ background: '#FFF', size: 'md' } as any))
const formatThemeSize = computed(() => {
  return { sm: '小', md: '默认', lg: '大', xl: '超大' }[theme.value.size]
})

function onChangeBackground(value: BACKGROUND) {
  emit('changeBackground', value)
}
function onChangeSize(zoom: number) {
  const sizes = Object.keys(THEME_SIZE_CLASSES) as SIZE[]
  const index = clamp(sizes.indexOf(theme.value.size) + zoom, 0, sizes.length - 1)
  emit('changeSize', sizes[index])
}
</script>

<template>
  <BasicPopup :value="value" :mask="false" @input="value => $emit('input', value)">
    <div class="w-full px-5 pt-25px py-10 shadow-2xl" :style="{ background: theme.background }">
      <div class="flex space-x-25px items-center">
        <p class="min-w-8 text-14px text-#999">
          背景
        </p>
        <div class="flex space-x-30px">
          <BizTrackClick
            v-for="item in THEME_BACKGROUNDS" :key="item"
            name="readPageClick" type="button" :extra="{ contentId, chapterId, button: theme.background }"
          >
            <span
              class="w-30px h-30px border rounded-full inline-block" :style="`background: ${item};`"
              :class="item === theme.background ? 'border-2 border-primary' : 'border-#ccc'"
              @click="onChangeBackground(item)"
            />
          </BizTrackClick>
        </div>
      </div>
      <div class="mt-5 flex space-x-25px items-center">
        <p class="min-w-8 text-14px text-#999">
          字号
        </p>
        <div class="flex-1 flex items-center justify-between">
          <BizTrackClick
            name="readPageClick" type="button"
            :extra="{ contentId, chapterId, button: 'A-' }"
            class="w-95px h-35px lh-35px text-center text-14px bg-#0000000A rounded"
            :class="{ 'text-#999': theme.size === 'sm' }" @click="onChangeSize(-1)"
          >
            A-
          </BizTrackClick>
          <span class="min-w-5 text-15px text-center">{{ formatThemeSize }}</span>
          <BizTrackClick
            name="readPageClick" type="button"
            :extra="{ contentId, chapterId, button: 'A+' }"
            class="w-95px h-35px lh-35px text-center text-14px bg-#0000000A rounded"
            :class="{ 'text-#999': theme.size === 'xl' }" @click="onChangeSize(1)"
          >
            A+
          </BizTrackClick>
        </div>
      </div>
      <div class="mt-5 flex items-center justify-between">
        <BizTrackClick
          name="readPageClick" type="button"
          :extra="{ contentId, chapterId, button: 'prevChapter' }"
          class="w-115px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
          :class="{ 'text-#999': !prev }" @click="prev && $emit('chapterPrev')"
        >
          上一章
        </BizTrackClick>
        <BizTrackClick
          name="readPageClick" type="button"
          :extra="{ contentId, chapterId, button: 'toc' }"
          class="w-80px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
          @click="$emit('toc')"
        >
          目录
        </BizTrackClick>
        <BizTrackClick
          name="readPageClick" type="button"
          :extra="{ contentId, chapterId, button: 'nextChapter' }"
          class="w-115px h-39px lh-39px text-center text-14px bg-#0000000A rounded"
          :class="{ 'text-#999': !next }" @click="next && $emit('chapterNext')"
        >
          下一章
        </BizTrackClick>
      </div>
    </div>
  </BasicPopup>
</template>
